import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useRouter } from 'next/router'

import { useWallet } from '@solana/wallet-adapter-react'
import { useWalletModal } from '@solana/wallet-adapter-react-ui'

// next components

// custom components
import ReadMoreModal from 'components/Modal/ReadMoreModal'
import ProfileArtistCard from 'components/Card/ProfileArtistCard'
import FollowModal from 'components/Modal/FollowModal'

// custom functions
import { useViewport } from 'utils'

// images and icons
import DefaultAvatar from 'assets/images/dashboard-avatar.png'

import { copyToClipboard, isLoggedIn } from 'utils/helpers'

import OutlineButton from 'components/Button/OutlineButton'
import SocialShareModal from 'components/Menu/SocialShareModal'
import Alert from 'components/Alert'

// api endpoint
import artistsAPI from 'apis/artists'
import NftImageEditorModal from 'components/Modal/NftImageEditorModal'

import ProfileInformationCard from 'components/Card/ProfileInformationCard'
import NFTDescriptionSection from 'components/pages/BrowseNFT/MainSection/NFTDescriptionSection'
import FollowModalMobile from 'components/Modal/FollowModalMobile'
import EditProfileModal from 'components/Modal/EditProfileModal'

const MainSection = prop => {
  const {
    artistInfo,
    artistStatsInfo,
    collectorStatsInfo,
    categoryList,
    isOwn,
    isLoaded,
    isColletorLoaded,
    setFullScreenView,
    wallet,
    reloadPage,
    hasLoading,
    setActiveTab,
    activeTab,
    refreshing,
    refreshNFT,
    updateProfile,
  } = prop
  const dispatch = useDispatch()
  const router = useRouter()
  const viewport = useViewport()
  const { publicKey } = useWallet()
  const { setVisible } = useWalletModal()

  const [alertData, setAlertData] = useState<any[]>([])
  const [showAlert, setShowAlert] = useState(false)

  const [openAvatarEditorModal, setOpenAvatarEditorModal] = useState(false)
  const [avatar, setAvatar] = useState<any>()
  const [croppedAvatar, setCroppedAvatar] = useState('')

  const [showEditProfile, setShowEditProfile] = useState(false)
  const [openShareModal, setOpenShareModal] = useState(false)
  const [showFollowModal, setShowFollowModal] = useState(false)
  const [showReadMore, setShowReadMore] = useState(false)
  const [type, setType] = useState(0)
  const [follow, setFollow] = useState(false)
  const [artistFollowing, setArtistFollowing] = useState(artistInfo?.following)
  const [artistFollowers, setArtistFollowers] = useState(artistInfo?.followers)
  const [walletList, setWalletList] = useState([])
  const [tempNftDescription, setTempNftDescription] = useState('')
  const [editingAvatar, setEditingAvatar] = useState(null)

  useEffect(() => {
    if (artistInfo?.bio !== undefined && artistInfo?.bio !== null) setTempNftDescription(artistInfo?.bio)
  }, [artistInfo?.bio])

  useEffect(() => {
    if (artistInfo) {
      updateProfileInfo(artistInfo)
    }
  }, [artistInfo])

  const updateProfileInfo = artistInfo => {
    if (artistInfo) {
      setFollow(artistInfo ? artistInfo.followed : false)
      setArtistFollowing(artistInfo.following)
      setArtistFollowers(artistInfo.followers)

      let list = []
      if (artistInfo?.twitter_url?.length > 0) list.push({ id: list.length, label: 'Twitter', type: 'twitter' })
      if (artistInfo?.drip_url?.length > 0) list.push({ id: list.length, label: 'DRiP', type: 'drip' })
      if (artistInfo?.ig_url?.length > 0) list.push({ id: list.length, label: 'Instagram', type: 'instagram' })
      if (artistInfo?.site_url?.length > 0) list.push({ id: list.length, label: 'Website', type: 'website' })
      if (artistInfo?.three_land_url?.length > 0) list.push({ id: list.length, label: '3.Land', type: 'land' })
      if (artistInfo?.solarplex_url?.length > 0) list.push({ id: list.length, label: 'Solarplex', type: 'solar' })
      setWalletList(list)

      if (
        artistInfo?.profile_picture !== null &&
        artistInfo?.profile_picture !== undefined &&
        artistInfo?.profile_picture !== ''
      ) {
        setCroppedAvatar(artistInfo?.profile_picture)
      } else {
        setCroppedAvatar('')
      }
    }
  }

  const followArtist = wallet => {
    if (isLoggedIn()) {
      artistsAPI.followArtist(wallet).then(response => {
        if (response && response.followed_wallet_id) {
          setFollow(true)
          setArtistFollowers(artistFollowers + 1)
        }
      })
    } else {
      dispatch({ type: 'set', loginAndRefresh: true })
      setVisible(true)
    }
  }

  const unfollowArtist = wallet => {
    artistsAPI.unfollowArtist(wallet).then(response => {
      setFollow(false)
      setArtistFollowers(artistFollowers - 1)
    })
  }

  const showAlertModal = () => {
    setAlertData([
      {
        id: 0,
        title: 'Max File Size Exceeded',
        description: `Oops! It looks like your file exceeded the max file size of 30mb`,
        type: 'error',
      },
    ])
    setShowAlert(true)
  }

  const showAlertCopyWallet = () => {
    setAlertData([
      {
        id: 0,
        title: 'Wallet address copied',
        description: `Your wallet address has been copied to your clipboard`,
        type: 'success',
      },
    ])
    setShowAlert(true)
  }

  const handleChangeAvatar = event => {
    const file = event.target.files
    if (file?.length === 0) return

    if (file[0].size > 10485760) {
      showAlertModal()
      return
    }
    setAvatar(file)
    setShowEditProfile(false)
    setTimeout(() => {
      setOpenAvatarEditorModal(true)
    }, 300)
  }

  return (
    <div>
      <div className="mt-[30px] lg:mt-[60px] w-full flex justify-between">
        <ProfileArtistCard
          image={croppedAvatar !== '' ? croppedAvatar : ''}
          name={artistInfo?.username}
          hasLoading={hasLoading}
          setFullScreenView={() =>
            setFullScreenView(croppedAvatar !== '' ? croppedAvatar : avatar !== undefined ? avatar : DefaultAvatar.src)
          }
          setOpenShareModal={setOpenShareModal}
          copyWalletAddress={() => {
            copyToClipboard(artistInfo?.wallet_id || '')
            showAlertCopyWallet()
          }}
        />
      </div>

      <div>
        {hasLoading ? (
          <div className="w-full h-[40px] rounded-[20px] overflow-hidden mt-[15px]">
            <div className="skeletonLoader" />
          </div>
        ) : artistInfo?.bio?.length > 0 ? (
          <div className="mt-[15px]">
            <NFTDescriptionSection description={tempNftDescription} />
          </div>
        ) : null}
        <div className="w-full flex justify-center mb-[30px]">
          <ProfileInformationCard
            isOwn={isOwn}
            hasLoading={hasLoading}
            setVisible={setVisible}
            setType={setType}
            setShowFollowModal={setShowFollowModal}
            artistFollowers={artistFollowers}
            artistFollowing={artistFollowing}
            walletList={walletList}
            artistInfo={artistInfo}
            isColletorLoaded={isColletorLoaded}
            artistStatsInfo={artistStatsInfo}
            collectorStatsInfo={collectorStatsInfo}
            refreshing={refreshing}
            refreshNFT={refreshNFT}
            follow={follow}
            setShowEditProfile={setShowEditProfile}
            followArtist={followArtist}
            unfollowArtist={unfollowArtist}
          />
        </div>
        {isOwn &&
          (viewport === 'xs' || viewport === 'sm' || viewport === 'md' ? (
            <div className="w-full flex flex-col gap-[7px]">
              <OutlineButton
                label="Artwork"
                borderColor={
                  activeTab === 0 ? 'rgba(var(--main-bg-color-rgb), 1)' : 'rgba(var(--main-bg-color-rgb), 0.1)'
                }
                backgroundColor="var(--bg-151515-E8E7E5)"
                color={activeTab === 0 ? 'rgba(var(--main-bg-color-rgb), 1)' : 'var(--bg-A5A5A5-606060)'}
                fontFamily="Jost-Regular"
                borderRadius={5}
                onClick={() => {
                  setActiveTab(0)
                }}
              />
              <div className="w-full flex gap-[5px]">
                <OutlineButton
                  label="Offers"
                  borderColor={
                    activeTab === 1 ? 'rgba(var(--main-bg-color-rgb), 1)' : 'rgba(var(--main-bg-color-rgb), 0.1)'
                  }
                  backgroundColor="var(--bg-151515-E8E7E5)"
                  borderRadius={5}
                  fullWidth
                  color={activeTab === 1 ? 'rgba(var(--main-bg-color-rgb), 1)' : 'var(--bg-A5A5A5-606060)'}
                  fontFamily="Jost-Regular"
                  onClick={() => {
                    setActiveTab(1)
                  }}
                />
                <OutlineButton
                  label="Favorites"
                  borderColor={
                    activeTab === 3 ? 'rgba(var(--main-bg-color-rgb), 1)' : 'rgba(var(--main-bg-color-rgb), 0.1)'
                  }
                  backgroundColor="var(--bg-151515-E8E7E5)"
                  borderRadius={5}
                  fullWidth
                  color={activeTab === 3 ? 'rgba(var(--main-bg-color-rgb), 1)' : 'var(--bg-A5A5A5-606060)'}
                  fontFamily="Jost-Regular"
                  onClick={() => {
                    setActiveTab(3)
                  }}
                />
              </div>
              <div className="w-full flex gap-[5px]">
                <OutlineButton
                  label="Activity"
                  borderColor={
                    activeTab === 2 ? 'rgba(var(--main-bg-color-rgb), 1)' : 'rgba(var(--main-bg-color-rgb), 0.1)'
                  }
                  backgroundColor="var(--bg-151515-E8E7E5)"
                  borderRadius={5}
                  fullWidth
                  color={activeTab === 2 ? 'rgba(var(--main-bg-color-rgb), 1)' : 'var(--bg-A5A5A5-606060)'}
                  fontFamily="Jost-Regular"
                  onClick={() => {
                    setActiveTab(2)
                  }}
                />
                <OutlineButton
                  label="Hidden"
                  borderColor={
                    activeTab === 4 ? 'rgba(var(--main-bg-color-rgb), 1)' : 'rgba(var(--main-bg-color-rgb), 0.1)'
                  }
                  backgroundColor="var(--bg-151515-E8E7E5)"
                  borderRadius={5}
                  fullWidth
                  color={activeTab === 4 ? 'rgba(var(--main-bg-color-rgb), 1)' : 'var(--bg-A5A5A5-606060)'}
                  fontFamily="Jost-Regular"
                  onClick={() => {
                    setActiveTab(4)
                  }}
                />
              </div>
            </div>
          ) : (
            <div className="w-full flex flex-col gap-[7px]">
              <OutlineButton
                label="Artwork"
                borderColor={
                  activeTab === 0 ? 'rgba(var(--main-bg-color-rgb), 1)' : 'rgba(var(--main-bg-color-rgb), 0.1)'
                }
                backgroundColor="var(--bg-151515-E8E7E5)"
                color={activeTab === 0 ? 'rgba(var(--main-bg-color-rgb), 1)' : 'var(--bg-A5A5A5-606060)'}
                fontFamily="Jost-Regular"
                borderRadius={5}
                onClick={() => {
                  setActiveTab(0)
                }}
              />
              <OutlineButton
                label="Offers"
                borderColor={
                  activeTab === 1 ? 'rgba(var(--main-bg-color-rgb), 1)' : 'rgba(var(--main-bg-color-rgb), 0.1)'
                }
                backgroundColor="var(--bg-151515-E8E7E5)"
                borderRadius={5}
                color={activeTab === 1 ? 'rgba(var(--main-bg-color-rgb), 1)' : 'var(--bg-A5A5A5-606060)'}
                fontFamily="Jost-Regular"
                onClick={() => {
                  setActiveTab(1)
                }}
              />
              <OutlineButton
                label="Activity"
                borderColor={
                  activeTab === 2 ? 'rgba(var(--main-bg-color-rgb), 1)' : 'rgba(var(--main-bg-color-rgb), 0.1)'
                }
                backgroundColor="var(--bg-151515-E8E7E5)"
                borderRadius={5}
                color={activeTab === 2 ? 'rgba(var(--main-bg-color-rgb), 1)' : 'var(--bg-A5A5A5-606060)'}
                fontFamily="Jost-Regular"
                onClick={() => {
                  setActiveTab(2)
                }}
              />
              <OutlineButton
                label="Favorites"
                borderColor={
                  activeTab === 3 ? 'rgba(var(--main-bg-color-rgb), 1)' : 'rgba(var(--main-bg-color-rgb), 0.1)'
                }
                backgroundColor="var(--bg-151515-E8E7E5)"
                borderRadius={5}
                color={activeTab === 3 ? 'rgba(var(--main-bg-color-rgb), 1)' : 'var(--bg-A5A5A5-606060)'}
                fontFamily="Jost-Regular"
                onClick={() => {
                  setActiveTab(3)
                }}
              />
              <OutlineButton
                label="Hidden"
                borderColor={
                  activeTab === 4 ? 'rgba(var(--main-bg-color-rgb), 1)' : 'rgba(var(--main-bg-color-rgb), 0.1)'
                }
                backgroundColor="var(--bg-151515-E8E7E5)"
                borderRadius={5}
                color={activeTab === 4 ? 'rgba(var(--main-bg-color-rgb), 1)' : 'var(--bg-A5A5A5-606060)'}
                fontFamily="Jost-Regular"
                onClick={() => {
                  setActiveTab(4)
                }}
              />
            </div>
          ))}
      </div>

      {showEditProfile && (
        <EditProfileModal
          open={showEditProfile}
          close={() => setShowEditProfile(false)}
          setAvatar={setAvatar}
          editingAvatar={editingAvatar}
          setEditingAvatar={setEditingAvatar}
          handleChangeAvatar={handleChangeAvatar}
          onSave={userInfo => {
            setShowEditProfile(false)
            updateProfile(userInfo)
          }}
        />
      )}

      {showFollowModal &&
        (viewport === 'xs' || viewport === 'sm' || viewport === 'md' ? (
          <FollowModalMobile
            open={showFollowModal}
            ownWallet={publicKey?.toBase58()}
            close={() => setShowFollowModal(false)}
            username={artistInfo?.username}
            type={type}
            wallet={artistInfo?.wallet_id}
          />
        ) : (
          <FollowModal
            open={showFollowModal}
            ownWallet={publicKey?.toBase58()}
            close={() => setShowFollowModal(false)}
            type={type}
            wallet={artistInfo.wallet_id}
            username={artistInfo.username}
          />
        ))}

      {showReadMore && (
        <ReadMoreModal
          open={showReadMore}
          close={() => setShowReadMore(false)}
          type={type}
          artistInfo={artistInfo}
          categoryList={categoryList}
        />
      )}

      {openShareModal && (
        <SocialShareModal
          open={openShareModal}
          close={() => setOpenShareModal(false)}
          vote={true}
          message={'Check out this artist on Foster Marketplace @foster_protocol #fostermarketplace'}
          url={`/${artistInfo?.username}`}
        />
      )}

      <NftImageEditorModal
        open={openAvatarEditorModal}
        close={() => setOpenAvatarEditorModal(false)}
        nftImage={avatar}
        getCroppedImage={croppedImage => {
          setEditingAvatar(croppedImage)
          setOpenAvatarEditorModal(false)
          setTimeout(() => {
            setShowEditProfile(true)
          }, 300)
        }}
        dimension={{ value: [512, 512] }}
      />
      <Alert
        open={showAlert}
        close={() => {
          setShowAlert(false)
        }}
        data={alertData}
      />
    </div>
  )
}

export default MainSection
